import { EventSourceInput } from '@fullcalendar/core'
import dateToTimeString from 'components/calendar/components'

import { isBefore } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import database from 'model'
import { updateEventOnServer } from 'services/api/event'
import Database from 'services/db/Database'
import {
  excludeInstance,
  localEventById,
  localEventByLocalId,
  RemoveTempEvents
} from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  AccountDoc,
  AggregatedDeliveryStatus,
  EventDoc,
  EventStatus,
  GroupDoc,
  LastMessage,
  ParticipantDataDoc
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { eventLengthInDays } from 'utils/event'
import {
  groupIdToProvider,
  isConvertedGroup,
  isConvertedGroupId,
  ProviderType
} from 'utils/groups'
import { isGuest } from 'utils/user'

import tick1 from '../../assets/icons/event-status/1tick.svg'
import tick2 from '../../assets/icons/event-status/2tick.svg'
import tick2Completed from '../../assets/icons/event-status/2tick_comp.svg'
import notsent from '../../assets/icons/event-status/not-sent.svg'
import { highestActiveTier } from './accounts'
import { isGroupOutOfTier, isUserCanOperateEvents } from './groups'
import {
  generateGroupcalArrayFromRecurrence,
  mapRecurrency
} from 'utils/recurrence'
import { isLateInstance, isSomeDayInstance } from './instance_state'
import { removePrefix } from 'utils/object'
import {
  buildEventsArrayForUIFromDBEvents,
  generateLateRecurrentInstances,
  generateLateTasksForEvents,
  prepareTasksForUI
} from 'utils/fc_mapping'

function formatEventLastUpdate(event: EventDoc): string {
  if (!event.StartDate || event.StartDate === 'null') return ''

  return dayjs(Number(event.StartDate) * 1000).format(
    event.AllDay === '1' ? 'L' : 'L LT'
  )
}

export function eventStatusIcon(event?: EventDoc): string {
  if (!event) return notsent as unknown as string

  const {
    AggregatedParticipantsDeliveryStatus,
    AggregatedParticipantsConfirmationStatus
  } = event

  if (event._id?.length === 0) return notsent as unknown as string
  if (
    AggregatedParticipantsConfirmationStatus != null &&
    AggregatedParticipantsConfirmationStatus.length > 0
  )
    return tick2Completed as unknown as string

  if (AggregatedParticipantsDeliveryStatus != null) {
    if (
      Number(AggregatedParticipantsDeliveryStatus) >=
      Number(AggregatedDeliveryStatus.DELIVERED)
    ) {
      return tick2 as unknown as string
    } else {
      return tick1 as unknown as string
    }
  }

  return notsent as unknown as string
}

function isEventNotAdded(event: EventDoc): boolean {
  return `${event?.Status}` === EventStatus.HARD_DELETE_FROM_DB
}

function isEventActive(event: EventDoc): boolean {
  return `${event?.Status}` === EventStatus.ACTIVE
}

function addEventMessage(event: EventDoc, message?: LastMessage): EventDoc {
  return {
    ...event,
    StartDate: String(event.StartDate),
    EndDate: String(event.EndDate),
    LastUpdateMessage: message
  }
}

export function updateEventDocMessage(
  userID: string,
  newEvent: EventDoc,
  prevEvent: EventDoc,
  participants: Model<ParticipantDataDoc>[]
): EventDoc {
  const isCurrentUserChangedEvent = newEvent.UserID === userID
  const lastUpdatedDate = formatEventLastUpdate(newEvent)
  const eventDetails = `${lastUpdatedDate} ${newEvent.Text}`
  const userMadeAction = participants.find(
    (participant) => participant.AccountID === newEvent.UserID
  )

  let userTitleMadeChange =
    userMadeAction?.FullName ?? userMadeAction?.PhoneNumber ?? ''

  if (userTitleMadeChange === 'null') {
    userTitleMadeChange = ''
  }

  if (isCurrentUserChangedEvent) {
    userTitleMadeChange = t('you')
  }

  const eventStart = dayjs(Number(newEvent.StartDate) * 1000)

  if (!prevEvent) {
    if (newEvent.OwnerID === userID) {
      return addEventMessage(newEvent, {
        title: newEvent.Text,
        type: `${t('eventAdded')}`,
        who: `${t('you')}`,
        when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
          eventStart.toDate(),
          true
        )}`
      })
    } else {
      return addEventMessage(newEvent, {
        title: newEvent.Text,
        type: `${t('eventAdded')}`,
        who: userTitleMadeChange,
        when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
          eventStart.toDate(),
          true
        )}`
      })
    }
  }

  if (prevEvent.LastUpdate !== newEvent.LastUpdate) {
    if (isEventNotAdded(prevEvent) && isEventNotAdded(newEvent)) {
      return addEventMessage(newEvent, {
        title: newEvent.Text,
        type: 'event not added, error',
        who: userTitleMadeChange,
        when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
          eventStart.toDate(),
          true
        )}`
      })
    } else if (isEventActive(prevEvent) && !isEventActive(newEvent)) {
      if (isCurrentUserChangedEvent) {
        return addEventMessage(newEvent, {
          title: newEvent.Text,
          type: `${t('eventDeleted')}`,
          who: userTitleMadeChange,
          when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
            eventStart.toDate(),
            true
          )}`
        })
      } else {
        return addEventMessage(newEvent, {
          title: newEvent.Text,
          type: `${t('eventDeleted')}`,
          who: userTitleMadeChange,
          when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
            eventStart.toDate(),
            true
          )}`
        })
      }
    } else {
      const isPrevEventUpdated = isBefore(
        new Date(Number(newEvent.LastUpdate) * 1000),
        new Date(Number(prevEvent.LastUpdate) * 1000)
      )

      if (prevEvent._rev === newEvent._rev && isPrevEventUpdated) {
        return addEventMessage(newEvent, {
          title: newEvent.Text,
          type: `notsaved`,
          who: userTitleMadeChange,
          when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
            eventStart.toDate(),
            true
          )}`
        })
      } else {
        return addEventMessage(newEvent, {
          title: newEvent.Text,
          type: `${t('eventUpdated')}`,
          who: userTitleMadeChange,
          when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
            eventStart.toDate(),
            true
          )}`
        })
      }
    }
  }

  return addEventMessage(newEvent, prevEvent.LastUpdateMessage ?? { type: '' })
}

export function buildAddEventMessage(userID: string, newEvent: EventDoc) {
  const eventStart = dayjs(Number(newEvent.StartDate) * 1000)

  if (newEvent.OwnerID === userID) {
    return addEventMessage(
      { ...newEvent, Status: EventStatus.ACTIVE },
      {
        title: newEvent.Text,
        type: `${t('eventAdded')}`,
        who: `${t('you')}`,
        when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
          eventStart.toDate(),
          true
        )}`
      }
    )
  } else {
    return addEventMessage(
      { ...newEvent, Status: EventStatus.ACTIVE },
      {
        title: newEvent.Text,
        type: `${t('eventAdded')}`,
        when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
          eventStart.toDate(),
          true
        )}`
      }
    )
  }
}

interface GroupStatus {
  outOfTierGroup: boolean
  canEdit: boolean
}

export function mapEventsToCalendar(
  events: Model<EventDoc>[] | EventDoc[],
  groups: Model<GroupDoc>[] | GroupDoc[],
  account: Model<AccountDoc> | AccountDoc
): EventSourceInput {
  const itemsForUI = buildEventsArrayForUIFromDBEvents(
    events,
    LocalStorage.get(LocalStorageKey.SOMEDAY_DATE) ?? '',
    LocalStorage.get(LocalStorageKey.SOMEDAY_ALL_DAY) ?? ''
  )
  const groupStatusMap = new Map<string, GroupStatus>()

  itemsForUI.forEach((event) => {
    const group = groups?.find((group) => group._id === event.GroupID)

    if (group && !groupStatusMap.has(event.GroupID ?? '')) {
      if (group) {
        const outOfTierGroup = isGroupOutOfTier(
          highestActiveTier(account),
          group,
          account
        )

        let canEdit = isUserCanOperateEvents(
          group,
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        )

        if (group && isConvertedGroup(group)) {
          canEdit = removePrefix(group?._id ?? '').includes(event.OwnerID ?? '')

          if (event.Organizer) {
            canEdit = event.Organizer.email === removePrefix(group?._id ?? '')
          }
        }

        groupStatusMap.set(event.GroupID ?? '', { canEdit, outOfTierGroup })
      }
    }
  })
  const mapped = itemsForUI
    .filter((event) => {
      if (isConvertedGroupId(event.GroupID)) {
        return (
          event.attendees
            ?.find((attendee) => attendee.self)
            ?.responseStatus?.toLowerCase() !== 'declined'
        )
      }

      //we ignore participant status in 24me app
      if (is24meApp()) return true

      const participantStatus =
        event.ParticipantsStatus?.[
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        ] ?? undefined

      return participantStatus?.ParticipantConfirmationStatus != '3'
    })
    .filter((event) => event.Status !== EventStatus.REMOVED)
    .map((event) => {
      const isAllDay = event.AllDay === '1' || event.late
      let startDate = dayjs(Number(event.StartDate) * 1000).tz(
        getDefaultTimezone()
      )

      let endDate = dayjs(Number(event.EndDate) * 1000).tz(getDefaultTimezone())

      if (isAllDay) {
        startDate = dayjs.utc(Number(event.StartDate) * 1000)
        endDate = dayjs.utc(Number(event.EndDate) * 1000)

        /**
         * because of mismatch between formats of all day events, we need to add extra day for our data source, in order to show
         * events correctly on a screen. The reason for that is that for example event which goes from October 9 to Octover 10
         * will be saved in our system as "from 2024-10-09 to 2024-10-10", when FullCalendar and some other providers support
         * format as such: "from 2024-10-09 to 2024-10-11", meaning, last day of the long event is day after */

        if (endDate.diff(startDate, 'days') > 0) {
          endDate = endDate.add(1, 'day').startOf('day')
        }

        /**
         * Because of a bug on iOS while adding all day events, they are added in devices timezone midnight as oppose to utc midnight, in order to fix that, we
         * need to add extra 13 hours to cover all timezones, and then make it start of the beginning of a day, we also can check the timezone which
         * will be empty for such events
         */

        if (
          event.TimeZoneNameID?.length === 0 ||
          event.TimeZoneNameID !== 'UTC'
        ) {
          startDate = startDate.add(13, 'hour').startOf('day')
          endDate = endDate.add(13, 'hour').startOf('day')
        }
      }

      let rrule: string | undefined = undefined

      try {
        rrule = mapRecurrency(event, true, false, getDefaultTimezone())
      } catch (e) {
        console.error(e)
      }

      if (rrule?.length == 0) {
        rrule = undefined
      }

      const outOfTierGroup = groupStatusMap.get(
        event.GroupID ?? ''
      )?.outOfTierGroup

      let canEdit = groupStatusMap.get(event.GroupID ?? '')?.canEdit

      const minutes = endDate.diff(startDate, 'minutes')

      const eventLen = eventLengthInDays(event) + 1

      const data = {
        end: endDate.format(isAllDay ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'),
        id: `${event.late}${event._id}`,
        title: event.Text,
        start: startDate.format(
          isAllDay ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'
        ),
        allDay: isAllDay || event.late,
        durationEditable:
          is24meApp() ||
          (!isGuest() &&
            (event._id?.length ?? 0) > 0 &&
            event.someday != '1' &&
            canEdit &&
            !outOfTierGroup &&
            (event.GroupID ===
              LocalStorage.get(LocalStorageKey.SELECTED_GROUP) ||
              !LocalStorage.get(LocalStorageKey.SELECTED_GROUP))),
        editable:
          is24meApp() ||
          (!isGuest() &&
            canEdit &&
            !outOfTierGroup &&
            (event.GroupID ===
              LocalStorage.get(LocalStorageKey.SELECTED_GROUP) ||
              !LocalStorage.get(LocalStorageKey.SELECTED_GROUP))),
        eventModel: event,
        rrule: event.late ? undefined : rrule,
        duration: {
          days: rrule && isAllDay ? eventLen : undefined,
          minutes: rrule && isAllDay ? undefined : minutes > 0 ? minutes : 20
        }
      }

      return data
    })

  return mapped
}

export async function handleEventUpdate(
  event: Model<EventDoc>,
  data: EventDoc,
  shouldSaveLocally: boolean,
  isDirty: boolean
): Promise<void> {
  /**
   * This field determines, if an event should be updated locally only, without touching the server
   * For example, we save reminders only locally
   */

  Logger.blue('EVENT_UPDATE', event, data, shouldSaveLocally, isDirty)
  const assignFields =
    (ignoreRecurrence: boolean, data: EventDoc) =>
    (localEvent: Model<EventDoc>) => {
      const now = Date.now().valueOf() / 1000
      localEvent.LastUpdate = String(now)
      localEvent.RequestConfirmation = data.RSVP

      //in case of ignore recurrence, we remove it from data object
      if (ignoreRecurrence) {
        delete data['Recurrence']
      }

      Object.keys(data).forEach((key) => {
        if (key !== 'id') localEvent[key] = data[key]
      })
    }

  return localEventByLocalId(event.id).then(async (localEvent) => {
    if (shouldSaveLocally) {
      await database.write(async () => {
        await localEvent.update(assignFields(false, data))
      })
    } else {
      if (
        isDirty ||
        data.Status === EventStatus.DELETED ||
        data.attendees
          ?.find((attendee) => attendee.self)
          ?.responseStatus?.toLowerCase() === 'declined'
      ) {
        return await database
          .write(async () => {
            if (groupIdToProvider(data.GroupID ?? '') !== ProviderType.GOOGLE) {
              await localEvent.update(assignFields(false, data))
            } else {
              await Promise.resolve()
            }
          })
          .then(() => {
            return updateEventOnServer(event, data)
          })
          .then(() => {
            if (
              data.attendees
                ?.find((attendee) => attendee.self)
                ?.responseStatus?.toLowerCase() === 'declined'
            ) {
              return Database.write(async () => {
                return event.destroyPermanently()
              })
            }
          })
      }
    }
  })
}

export async function excludeInstanceFromRecurrence(
  data: EventDoc,
  event: Model<EventDoc> | EventDoc,
  instanceStart: number,
  instanceEnd: number
) {
  LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
  const excluded = await excludeInstance(
    instanceStart,
    await localEventById(event._id ?? ''),
    isGroupcalApp()
      ? EventStatus.REMOVED
      : data.Status === EventStatus.ACTIVE
        ? EventStatus.DELETED
        : (data.Status ?? EventStatus.DELETED)
  )

  Logger.debug('excluded', excluded)

  await updateEventOnServer(excluded, {
    ...data,
    Recurrence: excluded.Recurrence,
    Status: EventStatus.ACTIVE, // to keep original series state
    StartDate: excluded.StartDate, // to keep original start series
    EndDate: excluded.EndDate, // to keep original start series
    Text: excluded.Text // to keep original start series
  })
}

export function isLate(event: EventDoc, ignoreRecurrence?: boolean) {
  return isLateInstance(event, ignoreRecurrence)
}

export function isSomeDay(event: EventDoc) {
  return isSomeDayInstance(event)
}

export function buildEventsArrayForUIFromDBEventsWithRecurrentEvents(
  events: EventDoc[],
  from: Dayjs,
  to: Dayjs
) {
  let itemsForUI: EventDoc[] = []

  prepareTasksForUI(
    events,
    LocalStorage.get(LocalStorageKey.SOMEDAY_DATE) ?? '',
    LocalStorage.get(LocalStorageKey.SOMEDAY_ALL_DAY) ?? ''
  ).forEach((event) => {
    const parsedItems = generateGroupcalArrayFromRecurrence(
      event,
      false,
      from,
      to
    )

    itemsForUI.push(...parsedItems)
  })

  if (is24meApp()) {
    itemsForUI = [
      ...itemsForUI,
      ...generateLateTasksForEvents(
        itemsForUI.filter(
          (event) => isLate(event, true) && (event._id?.length ?? 0 > 0)
        )
      ),
      ...generateLateRecurrentInstances(events)
    ]
  }
  return itemsForUI
}

export const eventComparator = (o1: EventDoc, o2: EventDoc): number => {
  const event1AllDay = o1.AllDay === '1' || o1.late ? '1' : '0'
  const event2AllDay = o2.AllDay === '1' || o2.late ? '1' : '0'

  return (
    // Compare by status
    Number(o1.Status) - Number(o2.Status) ||
    // Compare by allDay
    Number(event2AllDay) - Number(event1AllDay) ||
    // Compare by start time
    (o1.lateOriginalStartTime ?? o1.StartDate ?? '').localeCompare(
      o2.lateOriginalEndTime ?? o2.StartDate ?? ''
    ) ||
    // Compare by rank
    (o1.Rank ?? '').localeCompare(o2.Rank ?? '')
  )
}

export function saveEventToLocalDB(event: EventDoc) {
  return RemoveTempEvents().then(() => {
    return Database.write(async () => {
      return Database.getCollection<EventDoc>(CollectionType.EVENT).create(
        (localEvent) => {
          Object.keys(event).forEach((key) => {
            if (key !== 'id') localEvent[key] = event[key]
          })
        }
      )
    })
  })
}
